import {
	getDatabase,
	ref,
	get,
	query,
	orderByKey,
	startAt,
	endAt,
} from "firebase/database";

import fire from "../config/fire";
import ngeohash from "ngeohash";
import { autoDeleteExpiredOffers } from "../components/adminDashboard/autoDeleteExpiredOffers";

const db = getDatabase(fire);

/* ======================================================
   FETCH SHOP OFFERS
====================================================== */

export const fetchShopOffers = async (shopId) => {
	try {
		const snapshot = await get(ref(db, `offers/byShop/${shopId}`));

		if (!snapshot.exists()) return [];

		const offers = Object.values(snapshot.val());

		offers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

		return offers;
	} catch (error) {
		console.error(error);

		return [];
	}
};

/* ======================================================
   GET NEARBY GEOHASH PREFIXES
====================================================== */

const getNearbyGeoHashes = (lat, lng, precision = 4) => {
	try {
		const currentHash = ngeohash.encode(lat, lng, precision);

		const neighbors = ngeohash.neighbors(currentHash);

		return [currentHash, ...neighbors];
	} catch (error) {
		console.error(error);
		return [];
	}
};

/* ======================================================
   DISTANCE CALCULATION
====================================================== */

const calculateDistance = (userLat, userLng, shopLat, shopLng) => {
	const R = 6371;

	const dLat = ((shopLat - userLat) * Math.PI) / 180;

	const dLng = ((shopLng - userLng) * Math.PI) / 180;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((userLat * Math.PI) / 180) *
			Math.cos((shopLat * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

/* ======================================================
   FILTER DISTANCE
====================================================== */

export const filterDistanceOffers = (offers, userLocation, maxDistance = 5) => {
	if (!userLocation) return offers;

	return (
		offers
			.map((offer) => {
				if (!offer.location) return null;

				const distance = calculateDistance(
					userLocation.lat,
					userLocation.lng,
					offer.location.lat,
					offer.location.lng,
				);

				return {
					...offer,

					/* ======================================================
				   ADD CALCULATED DISTANCE
				====================================================== */

					calculatedDistance: Number(distance.toFixed(1)),
				};
			})

			/* ======================================================
		   REMOVE NULL VALUES
		====================================================== */

			.filter(Boolean)

			/* ======================================================
		   DISTANCE FILTER
		====================================================== */

			.filter((offer) => offer.calculatedDistance <= maxDistance)
	);
};

/* ======================================================
   FETCH OFFERS FROM GEOHASH PREFIXES
   ONLY FETCH NEARBY DATA
====================================================== */

const fetchNearbyGeoHashOffers = async (userLocation) => {
	try {
		if (!userLocation) return [];

		/* ======================================================
		   CREATE USER GEOHASH
		====================================================== */

		const precision = 4;

		const nearbyPrefixes = getNearbyGeoHashes(
			userLocation.lat,
			userLocation.lng,
			precision,
		);

		/* ======================================================
		   FETCH ONLY MATCHING PREFIXES
		====================================================== */

		let geoHashData = {};

		for (const prefix of nearbyPrefixes) {
			try {
				const geoQuery = query(
					ref(db, "offers/byGeoHash"),
					orderByKey(),
					startAt(prefix),
					endAt(prefix + "\uf8ff"),
				);

				const snapshot = await get(geoQuery);

				if (snapshot.exists()) {
					Object.assign(geoHashData, snapshot.val());
				}
			} catch (error) {
				console.log("GeoHash Fetch Error:", error);
			}
		}

		/* ======================================================
		   NO DATA
		====================================================== */

		if (!Object.keys(geoHashData).length) {
			return [];
		}

		/* ======================================================
		   EXTRACT OFFERS
		====================================================== */

		let offers = [];

		Object.values(geoHashData).forEach((hashNode) => {
			const hashOffers = Object.values(hashNode);

			offers.push(...hashOffers);
		});

		/* ======================================================
		   REMOVE DUPLICATES
		====================================================== */

		const uniqueOffersMap = {};

		offers.forEach((offer) => {
			if (offer?.offerId) {
				uniqueOffersMap[offer.offerId] = offer;
			}
		});

		let uniqueOffers = Object.values(uniqueOffersMap);

		// Delete expired offers in background
		autoDeleteExpiredOffers(uniqueOffers);

		// Remove expired offers immediately from response
		uniqueOffers = uniqueOffers.filter(
			(offer) => !offer.expiresAt || offer.expiresAt >= Date.now(),
		);

		return uniqueOffers;
	} catch (error) {
		console.log(error);
		return [];
	}
};

/* ======================================================
   FETCH FILTERED OFFERS
====================================================== */

export const fetchFilteredOffers = async ({
	category = "all",
	subCategory = "all",
	userLocation = null,
	distanceKm = 10,
}) => {
	try {
		/* ======================================================
			   FETCH ONLY NEARBY OFFERS
			====================================================== */

		let offers = await fetchNearbyGeoHashOffers(userLocation);

		/* ======================================================
			   DISTANCE FILTER
			====================================================== */

		offers = filterDistanceOffers(offers, userLocation, distanceKm);

		/* ======================================================
			   CATEGORY FILTER
			====================================================== */

		if (category !== "all") {
			const cleanCategory = category.toLowerCase().replace(/\s+/g, "_");

			offers = offers.filter((item) => item.category === cleanCategory);
		}

		/* ======================================================
			   SUB CATEGORY FILTER
			====================================================== */

		if (subCategory !== "all") {
			const cleanSubCategory = subCategory
				.toLowerCase()
				.replace(/\s+/g, "_");

			offers = offers.filter(
				(item) => item.subCategory === cleanSubCategory,
			);
		}

		/* ======================================================
			   SORT LATEST FIRST
			====================================================== */

		offers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

		return offers;
	} catch (error) {
		console.error(error);
		return [];
	}
};

/* ======================================================
   FETCH HOME OFFERS
====================================================== */

export const fetchHomeOffers = async (
	userLocation,
	distanceKm = 5,
	selectedCategory = "all",
	selectedSubCategory = "all",
) => {
	try {
		/* ======================================================
		   VALIDATE LOCATION
		====================================================== */

		if (!userLocation) {
			return {
				categorySections: [],
				categories: [],
			};
		}

		/* ======================================================
		   FETCH ONLY NEARBY OFFERS
		   BASED ON USER GEOHASH PREFIX
		====================================================== */

		let allNearbyOffers = await fetchNearbyGeoHashOffers(userLocation);

		/* ======================================================
		   DISTANCE FILTER
		====================================================== */

		allNearbyOffers = filterDistanceOffers(
			allNearbyOffers,
			userLocation,
			distanceKm,
		);

		/* ======================================================
		   SORT LATEST FIRST
		====================================================== */

		allNearbyOffers.sort(
			(a, b) => new Date(b.createdAt) - new Date(a.createdAt),
		);

		/* ======================================================
		   BUILD CATEGORY + SUBCATEGORY LIST
		   IMPORTANT:
		   BUILD FROM ALL NEARBY OFFERS
		====================================================== */

		const categoriesMap = {};

		allNearbyOffers.forEach((offer) => {
			const category = offer.category || "other";

			if (!categoriesMap[category]) {
				categoriesMap[category] = {
					category,
					subCategories: new Set(),
				};
			}

			if (offer.subCategory && offer.subCategory !== "all") {
				categoriesMap[category].subCategories.add(offer.subCategory);
			}
		});

		const categories = Object.values(categoriesMap).map((item) => ({
			category: item.category,
			subCategories: Array.from(item.subCategories),
		}));

		/* ======================================================
		   APPLY CATEGORY FILTER
		====================================================== */

		let filteredOffers = [...allNearbyOffers];

		if (selectedCategory !== "all") {
			const cleanCategory = selectedCategory
				.toLowerCase()
				.replace(/\s+/g, "_");

			filteredOffers = filteredOffers.filter(
				(item) => item.category === cleanCategory,
			);
		}

		/* ======================================================
		   APPLY SUBCATEGORY FILTER
		====================================================== */

		if (selectedSubCategory !== "all") {
			const cleanSubCategory = selectedSubCategory
				.toLowerCase()
				.replace(/\s+/g, "_");

			filteredOffers = filteredOffers.filter(
				(item) => item.subCategory === cleanSubCategory,
			);
		}

		/* ======================================================
		   PICK ONLY 1 LATEST OFFER PER SHOP
		====================================================== */

		const latestShopOffersMap = {};

		filteredOffers.forEach((offer) => {
			if (!offer.shopId) return;

			const existingOffer = latestShopOffersMap[offer.shopId];

			if (!existingOffer) {
				latestShopOffersMap[offer.shopId] = offer;

				return;
			}

			const existingDate = new Date(existingOffer.createdAt);

			const currentDate = new Date(offer.createdAt);

			if (currentDate > existingDate) {
				latestShopOffersMap[offer.shopId] = offer;
			}
		});

		const latestShopOffers = Object.values(latestShopOffersMap);

		/* ======================================================
		   GROUP BY CATEGORY
		   MAX 10 OFFERS
		====================================================== */

		const categoryMap = {};

		latestShopOffers.forEach((offer) => {
			const category = offer.category || "other";

			if (!categoryMap[category]) {
				categoryMap[category] = [];
			}

			if (categoryMap[category].length < 10) {
				categoryMap[category].push(offer);
			}
		});

		/* ======================================================
		   FINAL CATEGORY SECTION
		====================================================== */

		const categorySections = Object.keys(categoryMap).map((category) => ({
			category,
			offers: categoryMap[category],
		}));

		return {
			categorySections,
			categories,
		};
	} catch (error) {
		console.log(error);

		return {
			categorySections: [],
			categories: [],
		};
	}
};

/* ======================================================
   FETCH OFFER LIST
====================================================== */

export const fetchOfferList = async ({
	category = "all",
	subCategory = "all",
	userLocation = null,
	distanceKm = 5,
}) => {
	try {
		/* ======================================================
		   FETCH ONLY NEARBY OFFERS
		====================================================== */

		let offers = await fetchNearbyGeoHashOffers(userLocation);

		/* ======================================================
		   DISTANCE FILTER
		====================================================== */

		offers = filterDistanceOffers(offers, userLocation, distanceKm);

		/* ======================================================
		   CATEGORY FILTER
		====================================================== */

		if (category !== "all") {
			const cleanCategory = category.toLowerCase().replace(/\s+/g, "_");

			offers = offers.filter((item) => item.category === cleanCategory);
		}

		/* ======================================================
		   SUB CATEGORY FILTER
		====================================================== */

		if (subCategory !== "all") {
			const cleanSubCategory = subCategory
				.toLowerCase()
				.replace(/\s+/g, "_");

			offers = offers.filter(
				(item) => item.subCategory === cleanSubCategory,
			);
		}

		/* ======================================================
		   SORT
		====================================================== */

		offers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

		return offers;
	} catch (error) {
		console.error(error);
		return [];
	}
};

export const getOfferClaims = async (offerId) => {
	try {
		const snapshot = await get(ref(db, `claims/${offerId}`));

		if (!snapshot.exists()) {
			return [];
		}

		const data = snapshot.val();

		return Object.values(data);
	} catch (error) {
		console.error(error);
		return [];
	}
};
